// src/utils/geolocation.js
// Browser geolocation capture for attendance check-in / check-out.
//
// Location is a nice-to-have, never a gate: every failure path resolves with
// `{ coords: null, reason }` so the caller can still check the employee in.

export const LOCATION_STATUS = {
  IN_OFFICE: "in-office",
  OUT_OFFICE: "out-office",
  UNAVAILABLE: "location-unavailable",
};

export const LOCATION_LABELS = {
  [LOCATION_STATUS.IN_OFFICE]: "In Office",
  [LOCATION_STATUS.OUT_OFFICE]: "Out of Office",
  [LOCATION_STATUS.UNAVAILABLE]: "Location Unavailable",
};

const DEFAULT_TIMEOUT_MS = 30000;

// Remembers that this person has seen the location privacy notice, so it is
// shown once rather than before every check-in.
const PRIVACY_NOTICE_KEY = "ems.locationPrivacyNoticeSeen";

export const hasSeenLocationNotice = () => {
  try {
    return localStorage.getItem(PRIVACY_NOTICE_KEY) === "true";
  } catch {
    // Private windows and blocked site data throw on access. Falling back to
    // "not seen" shows the notice again, which is the safe direction.
    return false;
  }
};

export const markLocationNoticeSeen = () => {
  try {
    localStorage.setItem(PRIVACY_NOTICE_KEY, "true");
  } catch {
    /* nothing to do - the notice will simply show again next time */
  }
};

/**
 * Ask the browser for the current position.
 *
 * Always resolves - it never rejects - so a denied permission or a timeout
 * cannot break the check-in flow.
 *
 * @returns {Promise<{coords: {latitude:number, longitude:number, accuracy:number}|null, reason: string|null}>}
 */
export const captureCurrentPosition = ({ timeout = DEFAULT_TIMEOUT_MS } = {}) =>
  new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ coords: null, reason: "Location is not supported by this browser" });
      return;
    }

    let settled = false;
    const finish = (payload) => {
      if (settled) return;
      settled = true;
      resolve(payload);
    };

    // Belt and braces: some browsers ignore the positionOptions timeout.
    const timer = setTimeout(
      () => finish({ coords: null, reason: "Timed out while getting your location" }),
      timeout + 1000
    );

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timer);
        finish({
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          },
          reason: null,
        });
      },
      (error) => {
        clearTimeout(timer);
        const reasons = {
          1: "Location permission was denied",
          2: "Your location is currently unavailable",
          3: "Timed out while getting your location",
        };
        finish({ coords: null, reason: reasons[error?.code] || "Could not read your location" });
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    );
  });

/**
 * CSS modifier + label for a location status returned by the API.
 */
export const describeLocationStatus = (status) => {
  switch (status) {
    case LOCATION_STATUS.IN_OFFICE:
      return { label: LOCATION_LABELS[LOCATION_STATUS.IN_OFFICE], tone: "in-office" };
    case LOCATION_STATUS.OUT_OFFICE:
      return { label: LOCATION_LABELS[LOCATION_STATUS.OUT_OFFICE], tone: "out-office" };
    case LOCATION_STATUS.UNAVAILABLE:
      return { label: LOCATION_LABELS[LOCATION_STATUS.UNAVAILABLE], tone: "unavailable" };
    default:
      return null;
  }
};

/**
 * Human readable distance for the UI ("120 m", "1.4 km").
 */
export const formatDistance = (meters) => {
  const value = Number(meters);
  if (!Number.isFinite(value)) return null;
  if (value < 1000) return `${Math.round(value)} m`;
  return `${(value / 1000).toFixed(1)} km`;
};

/**
 * "12.971600, 77.594600" for display when no address has been resolved.
 */
export const formatCoordinates = (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

/**
 * Link to the point on a map, or null when there is nothing to plot.
 */
export const mapLink = (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

/**
 * Build the location detail for one attendance record.
 *
 * Handles the case the original release missed: an out-of-office record has
 * no matched office, so describing it by office name alone left the viewer
 * with "No office matched" and nothing else. Here the captured position
 * itself is the description - resolved address when the API has one,
 * coordinates otherwise - with the distance to the nearest office as context.
 *
 * @param {object} record - an attendance row from /status or /history
 * @param {"check_in"|"check_out"} which - which leg of the record to describe
 */
export const describeRecordLocation = (record = {}, which = "check_in") => {
  const status = which === "check_out"
    ? record.check_out_location_status
    : record.location_status;

  const described = describeLocationStatus(status);
  if (!described) return null;

  const latitude = record[`${which}_latitude`];
  const longitude = record[`${which}_longitude`];
  const address = record[`${which}_address`];

  const coordinates = formatCoordinates(latitude, longitude);
  const distance = formatDistance(record.distance_meters);
  const place = address || coordinates;

  // Only the check-in leg carries the matched office and confidence flag.
  const officeName = which === "check_in" ? record.office_name : null;
  const lowConfidence = which === "check_in" ? Boolean(record.low_confidence) : false;

  let detail = null;
  if (status === LOCATION_STATUS.IN_OFFICE) {
    detail = officeName
      ? (distance ? `${officeName} · ${distance} away` : officeName)
      : place;
  } else if (status === LOCATION_STATUS.OUT_OFFICE) {
    const parts = [];
    if (place) parts.push(place);
    if (distance && officeName) parts.push(`${distance} from ${officeName}`);
    else if (distance) parts.push(`${distance} from the nearest office`);
    detail = parts.length ? parts.join(" · ") : null;
  }
  // location-unavailable has no position by definition - nothing to add.

  return {
    ...described,
    status,
    detail,
    address: address || null,
    coordinates,
    distance,
    officeName,
    lowConfidence,
    accuracy: record[`${which}_accuracy`] ?? null,
    mapUrl: mapLink(latitude, longitude),
    // Everything a tooltip needs, in one string.
    title: [described.label, detail].filter(Boolean).join(" — ")
      + (lowConfidence ? " (low GPS confidence)" : ""),
  };
};
